import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { updateProfile } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solaro-founding/artifacts/6pe0ovti_Solaro%20Capri%20Gin%20-%20Horizontal%20Vectorial%20Handpainted%20PNG%20TRANSPARENT.png";

export default function SettingsPage() {
  const { user, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [whatsapp, setWhatsapp] = useState(user?.whatsapp || "");
  const [address, setAddress] = useState(user?.address || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ whatsapp: whatsapp || null, address: address || null });
      await fetchUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setSaving(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await updateProfile({ current_password: currentPassword, new_password: newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen" data-testid="settings-page">
      <header className="bg-white/90 backdrop-blur-md border-b border-[#D1CFC7] px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="Solaro" className="h-8 sm:h-10 object-contain" />
          <button
            data-testid="settings-back-btn"
            onClick={() => navigate("/grid")}
            className="text-xs uppercase tracking-widest text-[#002366] hover:text-[#D4AF37] font-body transition-colors flex items-center gap-1"
          >
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-3 sm:px-4 py-6 sm:py-10">
        <h1 className="font-display text-2xl sm:text-3xl text-[#002366] mb-2 tracking-tight">
          Settings
        </h1>
        <p className="font-body text-sm text-[#1A2B4C]/50 mb-8">
          Manage your profile, {user?.name}
        </p>

        {/* Profile info */}
        <form onSubmit={handleSaveProfile} className="bg-white p-5 sm:p-8 shadow-sm mb-6">
          <h2 className="font-display text-lg text-[#002366] mb-5">Contact Details</h2>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                Email <span className="normal-case tracking-normal text-[#1A2B4C]/30">(read-only)</span>
              </Label>
              <Input
                type="email"
                value={user?.email || ""}
                disabled
                className="border-[#D1CFC7] bg-[#F9F7F2] text-[#1A2B4C]/50"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                WhatsApp Number
              </Label>
              <Input
                data-testid="settings-whatsapp-input"
                type="text"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="+39 123 456 7890"
                className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                Shipping Address
              </Label>
              <Input
                data-testid="settings-address-input"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Street, City, Country"
                className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
              />
            </div>
          </div>

          <Button
            data-testid="settings-save-btn"
            type="submit"
            disabled={saving}
            className="mt-5 bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-xs px-6 py-5"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </form>

        {/* Change password */}
        <form onSubmit={handleChangePassword} className="bg-white p-5 sm:p-8 shadow-sm">
          <h2 className="font-display text-lg text-[#002366] mb-5">Change Password</h2>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                Current Password
              </Label>
              <Input
                data-testid="settings-current-pw-input"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
                className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                New Password
              </Label>
              <Input
                data-testid="settings-new-pw-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min. 6 characters"
                required
                minLength={6}
                className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                Confirm New Password
              </Label>
              <Input
                data-testid="settings-confirm-pw-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                required
                className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
              />
            </div>
          </div>

          <Button
            data-testid="settings-change-pw-btn"
            type="submit"
            disabled={saving}
            className="mt-5 bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-xs px-6 py-5"
          >
            {saving ? "Updating..." : "Update Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
